import React, { useCallback, useLayoutEffect, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { getFullscreenPortalTarget } from './fullscreenPortal';

export interface UnityGameDockAppProps {
  ariaLabel: string;
  /** Shown under the squircle (very small) */
  label?: string;
  icon: React.ReactNode;
  /** Headcount-style badge (top-right of squircle). */
  badge?: React.ReactNode;
  /** e.g. raised-hands indicator (bottom-left, opposite corner from `badge`). */
  secondaryBadge?: React.ReactNode;
  /** Visual highlight when popover/menu is open */
  active?: boolean;
  /** Toggle / pressed state (e.g. raise hand) */
  pressed?: boolean;
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  /** Close this app's popup when another dock app opens. */
  onRequestClose?: () => void;
  disabled?: boolean;
  /** Background for the squircle (Tailwind classes) */
  tileClassName: string;
  /** Extra classes for the caption under the squircle (e.g. wider / wrap) */
  labelClassName?: string;
  dataTourId?: string;
  /** Popover or extra UI; use `.unity-dock-popover` for panel chrome */
  children?: React.ReactNode;
  wrapperClassName?: string;
  /**
   * Set on the portaled anchor so parents can treat clicks inside the popover as "inside"
   * (e.g. `target.closest('[data-unity-dock-popover-owner="…"]')`) — required when portaling to body.
   */
  popoverOwnerId?: string;
}

const POPOVER_GAP_PX = 12;
const DOCK_APP_OPEN_EVENT = 'unityGameDockAppOpen';
let nextDockAppId = 0;

/**
 * Single “app” tile in the Unity game dock (squircle + optional caption).
 * When `children` are passed, they render in a fullscreen-aware portal with fixed positioning
 * beside the tile. (The dock tray's visual effects can break viewport-fixed descendants.)
 */
const UnityGameDockApp: React.FC<UnityGameDockAppProps> = ({
  ariaLabel,
  label,
  icon,
  badge,
  secondaryBadge,
  active,
  pressed,
  onClick,
  onRequestClose,
  disabled,
  tileClassName,
  labelClassName,
  dataTourId,
  children,
  wrapperClassName,
  popoverOwnerId,
}) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [dockAppId] = useState(() => `unity-dock-app-${++nextDockAppId}`);
  const [coords, setCoords] = useState<{ right: number; top: number } | null>(null);
  const hasPopover = children != null && children !== false;
  const isOpenRef = useRef(Boolean(active || hasPopover));
  const closeRef = useRef(onRequestClose);
  isOpenRef.current = Boolean(active || hasPopover);
  closeRef.current = onRequestClose;

  useEffect(() => {
    const closeWhenAnotherAppOpens = (event: Event) => {
      const openingAppId = (event as CustomEvent<string>).detail;
      if (openingAppId !== dockAppId && isOpenRef.current) {
        closeRef.current?.();
      }
    };
    window.addEventListener(DOCK_APP_OPEN_EVENT, closeWhenAnotherAppOpens);
    return () => window.removeEventListener(DOCK_APP_OPEN_EVENT, closeWhenAnotherAppOpens);
  }, [dockAppId]);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (!isOpenRef.current) {
      window.dispatchEvent(new CustomEvent(DOCK_APP_OPEN_EVENT, { detail: dockAppId }));
    }
    onClick?.(event);
  };

  const updateCoords = useCallback(() => {
    const w = wrapperRef.current;
    if (!w || !hasPopover) {
      setCoords(null);
      return;
    }
    const r = w.getBoundingClientRect();
    const centerY = r.top + r.height / 2;
    setCoords({
      right: window.innerWidth - r.left + POPOVER_GAP_PX,
      // Popovers can be tall. Keep their center away from both viewport edges.
      top: Math.min(window.innerHeight * 0.65, Math.max(window.innerHeight * 0.35, centerY)),
    });
  }, [hasPopover]);

  useLayoutEffect(() => {
    updateCoords();
  }, [updateCoords, children]);

  useEffect(() => {
    if (!hasPopover) return undefined;
    window.addEventListener('resize', updateCoords);
    window.addEventListener('scroll', updateCoords, true);
    return () => {
      window.removeEventListener('resize', updateCoords);
      window.removeEventListener('scroll', updateCoords, true);
    };
  }, [hasPopover, updateCoords]);

  const popoverNode =
    hasPopover && coords != null && typeof document !== 'undefined' ? (
      <div
        className="unity-dock-popover-anchor"
        {...(popoverOwnerId ? { 'data-unity-dock-popover-owner': popoverOwnerId } : {})}
        style={{
          position: 'fixed',
          right: coords.right,
          top: coords.top,
          transform: 'translateY(-50%)',
          zIndex: 10001,
          pointerEvents: 'auto',
        }}
      >
        {children}
      </div>
    ) : null;

  return (
    <div
      ref={wrapperRef}
      className={`relative flex shrink-0 flex-col items-center unity-dock-app-wrapper ${wrapperClassName || ''}`}
    >
      <button
        type="button"
        data-tour-id={dataTourId}
        aria-label={ariaLabel}
        title={ariaLabel}
        aria-pressed={pressed !== undefined ? pressed : undefined}
        disabled={disabled}
        onClick={handleClick}
        className="unity-dock-app-button group flex flex-col items-center gap-0.5 rounded-lg p-0.5 outline-none transition-transform focus-visible:ring-2 focus-visible:ring-white/60 focus-visible:ring-offset-2 focus-visible:ring-offset-black/40 disabled:cursor-not-allowed disabled:opacity-40"
      >
        <div
          className={`unity-dock-squircle relative flex h-12 w-12 items-center justify-center rounded-2xl shadow-md transition-transform duration-150 group-hover:scale-105 group-active:scale-95 ${tileClassName} ${
            active || pressed ? 'ring-2 ring-white/50 ring-offset-2 ring-offset-transparent' : ''
          }`}
        >
          <span className="text-white drop-shadow-sm [&>svg]:h-6 [&>svg]:w-6">{icon}</span>
          {badge != null && badge !== false && (
            <span className="unity-dock-badge absolute -right-0.5 -top-0.5 flex min-h-[1.125rem] min-w-[1.125rem] items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white shadow">
              {badge}
            </span>
          )}
          {secondaryBadge != null && secondaryBadge !== false && (
            <span className="unity-dock-badge-secondary absolute -bottom-0.5 -left-0.5 flex min-h-[1.125rem] min-w-[1.125rem] items-center justify-center gap-0.5 rounded-full bg-amber-500 px-0.5 text-[10px] font-bold text-white shadow ring-1 ring-amber-950/30">
              {secondaryBadge}
            </span>
          )}
        </div>
        {label ? (
          <span
            className={`max-w-[4.25rem] truncate text-center text-[10px] font-medium leading-tight text-white/85 ${labelClassName || ''}`}
          >
            {label}
          </span>
        ) : null}
      </button>
      {popoverNode ? createPortal(popoverNode, getFullscreenPortalTarget()) : null}
    </div>
  );
};

export default UnityGameDockApp;
